import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { Media, MediaType } from "@/types/media";
import { allMedia, filterMedia } from "@/utils/media";
import MediaCard from "@/components/feature/MediaCard";
import Pagination from "@/components/feature/Pagination";
import Breadcrumb from "@/components/feature/Breadcrumb";

const PAGE_SIZE = 8;

interface CatalogViewProps {
  type: MediaType;
}

function FilterRow({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: string[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex flex-col gap-2 py-3 md:flex-row md:items-start md:gap-4">
      <span className="shrink-0 pt-1 text-sm font-medium text-foreground-500 md:w-14">{label}</span>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => {
          const active = value === option;
          return (
            <button
              key={option}
              type="button"
              onClick={() => onChange(option)}
              className={`whitespace-nowrap rounded-md px-3 py-1 text-sm transition-colors ${
                active
                  ? "bg-primary-600 font-medium text-background-50"
                  : "bg-background-100 text-foreground-600 hover:bg-secondary-100 hover:text-secondary-800"
              }`}
            >
              {option}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function CatalogView({ type }: CatalogViewProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialGenre = searchParams.get("genre") ?? "全部";

  const [genre, setGenre] = useState(initialGenre);
  const [region, setRegion] = useState("全部");
  const [year, setYear] = useState("全部");
  const [episodes, setEpisodes] = useState("全部");
  const [sort, setSort] = useState("最新上映");
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  const baseList = useMemo(() => allMedia.filter((item) => item.type === type), [type]);
  const genres = ["全部", ...Array.from(new Set(baseList.flatMap((item) => item.genres)))];
  const regions = ["全部", ...Array.from(new Set(baseList.map((item) => item.region)))];
  const years = ["全部", ...Array.from(new Set(baseList.map((item) => String(item.year)))).sort((a, b) => Number(b) - Number(a))];
  const episodeOptions = ["全部", "20集以内", "20-50集", "50集以上"];
  const sortOptions = ["最新上映", "评分最高", "播放最多"];

  const result = useMemo(
    () => filterMedia({ type, genre, region, year, sort, episodes }),
    [type, genre, region, year, sort, episodes]
  );

  const totalPages = Math.max(1, Math.ceil(result.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = result.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const updateFilter = (setter: (v: string) => void) => (value: string) => {
    setter(value);
    setPage(1);
    if (value !== "全部" && setter === setGenre) {
      setSearchParams({ genre: value });
    } else if (setter === setGenre) {
      setSearchParams({});
    }
  };

  const resetAll = () => {
    setGenre("全部");
    setRegion("全部");
    setYear("全部");
    setEpisodes("全部");
    setSort("最新上映");
    setPage(1);
    setFilterOpen(false);
    setSearchParams({});
  };

  const title = type === "movie" ? "电影频道" : "电视剧频道";
  const description =
    type === "movie"
      ? "飘花电影网电影频道，汇集动作、喜剧、科幻、悬疑等类型的海量电影资源，支持按类型、地区、年份筛选，快速找到想看的电影。"
      : "飘花电影网电视剧频道，覆盖古装、悬疑、家庭、历史等热门剧集，支持按类型、地区、年份、集数筛选，追剧更轻松。";

  return (
    <div className="mx-auto w-full max-w-[1200px] px-4 py-6 md:px-6">
      <Breadcrumb items={[{ label: type === "movie" ? "电影" : "电视剧" }]} />

      <div className="mt-4 rounded-xl border border-background-200 bg-background-50 p-4 md:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-900 md:text-2xl">
              <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary-600 text-background-50">
                <i className={type === "movie" ? "ri-movie-2-line" : "ri-tv-2-line"} />
              </span>
              {title}
            </h1>
            <p className="mt-1.5 text-sm text-foreground-500">{description}</p>
          </div>
          <button
            type="button"
            onClick={() => setFilterOpen((v) => !v)}
            className="flex items-center gap-1 rounded-md border border-background-300 px-3 py-2 text-sm text-foreground-700 md:hidden"
          >
            <i className="ri-filter-3-line" />
            筛选
            <i className={filterOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
          </button>
        </div>
      </div>

      <div className={`mt-4 rounded-xl border border-background-200 bg-background-50 px-4 md:px-6 ${filterOpen ? "block" : "hidden md:block"}`}>
        <div className="divide-y divide-background-200 py-1">
          <FilterRow label="类型" options={genres} value={genre} onChange={updateFilter(setGenre)} />
          <FilterRow label="地区" options={regions} value={region} onChange={updateFilter(setRegion)} />
          <FilterRow label="年份" options={years} value={year} onChange={updateFilter(setYear)} />
          {type === "tv" && (
            <FilterRow label="集数" options={episodeOptions} value={episodes} onChange={updateFilter(setEpisodes)} />
          )}
          <FilterRow label="排序" options={sortOptions} value={sort} onChange={updateFilter(setSort)} />
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between">
        <p className="text-sm text-foreground-500">
          共找到 <span className="font-semibold text-primary-700">{result.length}</span> 部{type === "movie" ? "电影" : "电视剧"}
        </p>
        {(genre !== "全部" || region !== "全部" || year !== "全部" || episodes !== "全部" || sort !== "最新上映") && (
          <button
            type="button"
            onClick={resetAll}
            className="flex items-center gap-1 text-sm text-foreground-500 transition-colors hover:text-primary-700"
          >
            <i className="ri-refresh-line" />
            重置筛选
          </button>
        )}
      </div>

      {pageItems.length > 0 ? (
        <>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
            {pageItems.map((item) => (
              <MediaCard key={item.id} media={item} />
            ))}
          </div>
          <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
        </>
      ) : (
        <div className="mt-6 flex flex-col items-center justify-center rounded-xl border border-dashed border-background-300 bg-background-50 py-16">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-inbox-line text-3xl" />
          </span>
          <p className="mt-4 font-medium text-foreground-700">没有找到符合条件的{type === "movie" ? "电影" : "电视剧"}</p>
          <p className="mt-1 text-sm text-foreground-400">试试调整筛选条件，或查看全部内容</p>
          <div className="mt-5 flex gap-3">
            <button
              type="button"
              onClick={resetAll}
              className="whitespace-nowrap rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
            >
              重置筛选条件
            </button>
            <Link
              to="/"
              className="whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400"
            >
              返回首页
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}